import React from "react";
import { useTheme } from "../context/ThemeContext";

type LeftTableProps = {
  data: { label: string; value: string | number | React.ReactNode }[];
  margin?: string;
  title?: string;
};

const DEFAULT_PRIMARY = "#0c333c";

function hexToRgba(hex: string | undefined, alpha: number): string {
  const fallback = DEFAULT_PRIMARY;

  const clean = String(hex || fallback).replace("#", "");

  if (clean.length !== 6) {
    return `rgba(12, 51, 60, ${alpha})`;
  }

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if ([r, g, b].some((v) => Number.isNaN(v))) {
    return `rgba(12, 51, 60, ${alpha})`;
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const LeftTable: React.FC<LeftTableProps> = ({
  data,
  margin = "0",
  title = "Informações da O.S.",
}) => {
  const { theme } = useTheme();

  const primary = DEFAULT_PRIMARY;
  const themePrimary = theme?.primary || primary;

  const borderColor = hexToRgba(primary, 0.16);
  const labelBg = hexToRgba(primary, 0.075);
  const headerBg = primary;
  const subtleBg = "#f8fafb";

  return (
    <section
      style={{
        margin,
        width: "100%",
        boxSizing: "border-box",
        fontFamily:
          "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        border: `1px solid ${borderColor}`,
        backgroundColor: "#ffffff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          minHeight: 38,
          backgroundColor: headerBg,
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 14px",
          boxSizing: "border-box",
          borderBottom: `1px solid ${hexToRgba(primary, 0.22)}`,
        }}
      >
        <strong
          style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          {title}
        </strong>

        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            opacity: 0.72,
            boxShadow: `0 0 0 4px ${hexToRgba("#ffffff", 0.14)}`,
          }}
        />
      </div>

      <div
        style={{
          width: "100%",
          backgroundColor: subtleBg,
          padding: 8,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(160px, 240px) minmax(0, 1fr)",
            width: "100%",
            border: `1px solid ${borderColor}`,
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
          }}
        >
          {data.map((item, index) => {
            const isLast = index === data.length - 1;

            return (
              <React.Fragment key={`${item.label}-${index}`}>
                <div
                  style={{
                    minHeight: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    padding: "8px 12px",
                    boxSizing: "border-box",
                    backgroundColor: labelBg,
                    color: primary,
                    fontSize: 12.5,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    borderRight: `1px solid ${borderColor}`,
                    borderBottom: isLast ? "none" : `1px solid ${borderColor}`,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {item.label}
                </div>

                <div
                  style={{
                    minHeight: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    padding: "8px 14px",
                    boxSizing: "border-box",
                    backgroundColor: "#ffffff",
                    color: "#111827",
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.35,
                    borderBottom: isLast ? "none" : `1px solid ${borderColor}`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {item.value || "-"}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div
        style={{
          height: 3,
          background: `linear-gradient(90deg, ${primary}, ${themePrimary}, ${hexToRgba(
            primary,
            0.35
          )})`,
        }}
      />
    </section>
  );
};

export default LeftTable;
